import React, { useRef, useState, type ReactNode } from 'react';
import { UploadIcon } from '@radix-ui/react-icons';
import { Text, NativeFileInput, UnstyledButton } from '@/shared/ui';
import { SectionCard } from '@/features/property-management/components/PropertyDetailView/SectionCard';
import { DocumentRow, type DocumentRowDocument } from '../document-row/DocumentRow';
import { DocumentViewer } from '../document-viewer/DocumentViewer';

/** File extensions accepted by the upload input on both document tabs. */
const DEFAULT_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png';

export interface DocumentsTabProps<TDocument extends DocumentRowDocument> {
  documents: TDocument[];
  loading: boolean;
  error: string | null;
  onUpload: (file: File) => void;
  onDelete: (documentId: string) => void;
  onRename: (documentId: string, filename: string) => void;
  title: string;
  /** Already-resolved error message (i18n interpolation is the caller's responsibility). */
  errorMessage: string;
  emptyMessage: string;
  uploadLabel: string;
  /** i18n namespace/key-prefix forwarded to the shared `DocumentRow`. */
  i18nNamespace: string;
  i18nKeyPrefix: string;
  accept?: string;
  /** Extra content rendered above the loading/error/list states, e.g. registry metadata. */
  extraContent?: ReactNode;
}

/**
 * Generic documents tab: upload, list, rename and delete attached files.
 * Shared by the property and client detail views — they only differ in the
 * i18n copy, the accepted document shape, and optional extra content (the
 * property tab renders registry metadata above the file list).
 *
 * Structured so the card and its states live in one tree rather than in
 * early returns: the title stays on screen while loading or after a
 * failure instead of the panel swapping out entirely.
 *
 * @param props - Documents, loading/error state, handlers and display copy.
 * @returns Rendered documents tab.
 */
export const DocumentsTab = <TDocument extends DocumentRowDocument>({
  documents,
  loading,
  error,
  onUpload,
  onDelete,
  onRename,
  title,
  errorMessage,
  emptyMessage,
  uploadLabel,
  i18nNamespace,
  i18nKeyPrefix,
  accept = DEFAULT_ACCEPT,
  extraContent,
}: DocumentsTabProps<TDocument>) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [viewing, setViewing] = useState<TDocument | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUpload(file);
      e.target.value = '';
    }
  };

  const uploadButton = !loading && !error ? (
    <UploadDocumentButton
      label={uploadLabel}
      onClick={() => fileInputRef.current?.click()}
    />
  ) : null;

  return (
    <SectionCard title={title} titleAccessory={uploadButton}>
      {extraContent}

      {loading && (
        <div className="flex flex-col gap-(--space-2)">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-[44px] rounded-(--radius-md) bg-[var(--surface-tertiary)] animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && error && (
        <Text className="text-[length:var(--text-body-sm)] text-[var(--status-error)] m-0">
          {errorMessage}
        </Text>
      )}

      {!loading && !error && (
        <>
          <NativeFileInput
            ref={fileInputRef}
            className="hidden"
            onChange={handleFileChange}
            accept={accept}
          />
          <div className="flex flex-col gap-(--space-2)">
            {documents.length === 0 && (
              <div className="flex items-center justify-center h-[80px] text-[var(--text-tertiary)] text-[length:var(--text-body-sm)]">
                {emptyMessage}
              </div>
            )}
            {documents.map((doc) => (
              <DocumentRow<TDocument>
                key={doc.document_id}
                doc={doc}
                onRename={onRename}
                onDelete={onDelete}
                i18nNamespace={i18nNamespace}
                i18nKeyPrefix={i18nKeyPrefix}
                onOpen={setViewing}
              />
            ))}
          </div>
        </>
      )}

      <DocumentViewer<TDocument>
        doc={viewing}
        onClose={() => setViewing(null)}
        i18nNamespace={i18nNamespace}
        i18nKeyPrefix={i18nKeyPrefix}
      />
    </SectionCard>
  );
};

/**
 * Dashed call-to-action that opens the hidden file picker. Mirrors the
 * schedule-visit CTA on the visits tab so both section headers read the same.
 *
 * @param props - The button label and click handler.
 * @returns The rendered upload-document button.
 */
const UploadDocumentButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <UnstyledButton
    type="button"
    data-testid="documents-upload"
    onClick={onClick}
    aria-label={label}
    className="flex items-center gap-(--space-1-5) rounded-(--radius-md) border border-dashed border-[var(--border-subtle)] px-(--space-3) py-(--space-1-5) text-[var(--seed-base)] cursor-pointer bg-transparent [font-family:var(--font-family)] hover:bg-[var(--surface-hover)]"
  >
    <UploadIcon width={13} height={13} />
    <Text as="span" className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)]">{label}</Text>
  </UnstyledButton>
);

export default DocumentsTab;
